/**
 * THE SANCTIONS REVIEW DESK, read from the database.
 *
 * FIC Act s26A. Four tables, two functions and a screening trigger have existed
 * since script 29; ledger row H2 recorded that there was no admin control
 * surface for any of it. The reason turned out to be one layer down and no
 * amount of screen-writing would have reached it: every policy on the four
 * tables gates on `manage_sanctions_list`, and that permission was named by four
 * RLS policies and present in NO ROW of the permissions catalogue.
 *
 * Both routes by which a permission reaches a person — role_permissions and
 * admin_card_permissions — carry a foreign key to that catalogue, so the
 * permission could not be attached to a role or a card. It was held by nobody
 * and could be given to nobody. Twenty-nine policies across thirteen such
 * permissions were in that state; script 205 registers this one and names the
 * other twelve.
 *
 * TWO CALLS, AND THE SPLIT IS THE SPECIFICATION.
 *
 *   queue    — the work list. It carries the subject's name, so unlike the
 *              provider application queue this is NOT reason-free: opening the
 *              section is opening identifiable personal data joined to an
 *              allegation, and `manage_sanctions_list` is registered with
 *              requires_reason_capture true for that reason. The gate is the
 *              screen's, before the fetch.
 *
 *   verdict  — clear, confirm, or escalate to the FIC. The database refuses
 *              reasoning under twenty characters and refuses to edit a match
 *              that is already resolved, so this module cannot produce an
 *              anonymous verdict or a silent reversal even if it wanted to.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const fail = (error) => ({ data: null, error });

export const VERDICTS = [
  {
    key: 'false_positive_cleared',
    label: 'Not this person',
    // The common outcome by a wide margin, and the wording avoids "clear",
    // which reads as a judgement about the member rather than about the match.
    blurb: 'The screening matched a name, and the person in front of you is not the person on the list.',
  },
  {
    key: 'confirmed_match',
    label: 'This is the listed person',
    blurb: 'The subject and the listing are the same person. The match becomes evidence and the account is held.',
  },
  {
    key: 'escalated_to_fic',
    label: 'Escalate to the FIC',
    blurb: 'Refer to the Financial Intelligence Centre. This row is Orchid’s own record that the referral was made.',
  },
];

/** @param {'pending_compliance_review'|'confirmed_match'|'false_positive_cleared'|'escalated_to_fic'|null} status */
export async function fetchSanctionsQueue(status = 'pending_compliance_review') {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_sanctions_review_queue', {
    p_status: status,
  });
  if (error) return fail(error.message || 'The review queue could not be loaded.');
  return { data: data || [], error: null };
}

/**
 * Record a verdict.
 *
 * There is no version of this without reasoning, because there is no version of
 * the database function without it. Twenty characters is the database's floor;
 * saying so in the screen is kinder than letting somebody type "ok" and be
 * refused by Postgres.
 */
export async function recordVerdict({ matchId, verdict, notes }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_record_sanctions_verdict', {
    p_match_id: matchId,
    p_verdict: verdict,
    p_notes: notes,
  });
  if (error) return fail(error.message || 'That verdict could not be recorded.');
  return { data: Array.isArray(data) ? data[0] || null : data, error: null };
}

/** The database's own floor, named once so the screen and the check agree. */
export const MIN_VERDICT_NOTES = 20;

/**
 * Add the individuals named on an FIC notice to the sanctions list.
 *
 * THE NAMES ARE SENT, NEVER THE DOCUMENT. The PDF is read in the browser and
 * never leaves it: what crosses the wire is the list a compliance officer
 * ticked, which is also the only thing the database will accept.
 * fn_add_sanctions_list_entries has no parameter for a file and no idea one was
 * involved.
 *
 * EVERY REFUSAL BELOW IS THE DATABASE'S, NOT THIS FILE'S. It refuses a desk
 * without manage_sanctions_list, an upload with no notice reference, one with
 * no captured reason, a batch too large to have been reviewed, and a line that
 * is not a name. None of those is re-checked here — a second copy of a rule is
 * a second place for it to drift — but the screen states them before somebody
 * types rather than after.
 *
 * @param {object}   args
 * @param {Array}    args.entries      [{ fullName, dateOfBirth, nationality, designation, reference }]
 * @param {string}   args.sourceNotice the FIC notice reference
 * @param {string}   args.reason       Canon 4.2, recorded before the record
 */
export async function addSanctionsListEntries({ entries, sourceNotice, reason }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_add_sanctions_list_entries', {
    p_entries: (entries || []).map((e) => ({
      full_name: e.fullName,
      date_of_birth: e.dateOfBirth || null,
      nationality: e.nationality || null,
      designation: e.designation || null,
      reference: e.reference || null,
    })),
    p_source_notice: sourceNotice,
    p_reason: reason,
  });
  if (error) return fail(error.message || 'Those names could not be added to the list.');
  return { data: data || [], error: null };
}

/**
 * The database's ceiling on one upload, named once so the screen and the
 * function agree on the number rather than each carrying its own.
 */
export const MAX_NAMES_PER_UPLOAD = 2000;

/**
 * READ THE LIST BACK.
 *
 * The founder, 14 September: *"I see that I can add a name to the sanctioned
 * list, but there's nowhere for me to view all the names that were added
 * historically."* Exactly right, and one layer deeper than a missing screen:
 * `fn_screen_against_sanctions` MATCHES a name against the list and never
 * enumerates it, so until script 359 there was no function that could return a
 * page of it to anybody.
 *
 * `includeRemoved` is the whole history rather than the operating list. The
 * default is the operating list, because that is what "who is on it" means.
 *
 * @param {object}  [args]
 * @param {boolean} [args.includeRemoved] names already taken off, too
 * @param {number}  [args.limit]          the database caps this at 1000
 */
export async function sanctionsListEntries({ includeRemoved = false, limit = 200 } = {}) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_sanctions_list_entries', {
    p_include_removed: includeRemoved,
    p_limit: limit,
  });
  if (error) return fail(error.message || 'The sanctions list could not be read.');
  return { data: data || [], error: null };
}

/**
 * TAKE A NAME OFF, WITH PROOF.
 *
 * *"and remove any names — WITH PROOF, THAT MUST be attached."*
 *
 * Two required pieces, and they are different things. The REASON is Canon 4.2 —
 * why this desk is doing it, in their own words. The NOTICE REFERENCE is the
 * proof — the delisting notice the act rests on, symmetric with the
 * `source_notice_reference` that evidenced the addition. A removal with a
 * reason and no notice is somebody's opinion; the database refuses it, and this
 * file does not re-check that because a second copy of a rule is a second place
 * for it to drift.
 *
 * NOTHING IS DELETED. The entry's status becomes `delisted` and the row keeps
 * who took it off, when, why and on what notice.
 */
export async function removeFromSanctionsList({ entryId, reason, noticeReference }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_remove_from_the_sanctions_list', {
    p_entry_id: entryId,
    p_reason: reason,
    p_notice_reference: noticeReference,
  });
  if (error) return fail(error.message || 'That name could not be taken off the list.');
  return { data, error: null };
}
